import { Alert, Anchor, Button, Checkbox, Group, Modal, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGrantProductAccess,
  useOrgProductAssignments,
  useRevokeProductAccess,
} from '../api/products';

interface Props {
  opened: boolean;
  onClose: () => void;
  userId: string;
  membershipId: string;
  orgId: string;
  orgName: string;
  grantedProductIds: string[];
}

export function MembershipProductsModal({
  opened,
  onClose,
  userId,
  membershipId,
  orgId,
  orgName,
  grantedProductIds,
}: Props) {
  // Candidates are the org's active product assignments — only products
  // the org has been assigned should be grantable to its members
  // (spec §6.4 + §7.4).
  const assignmentsQuery = useOrgProductAssignments(orgId, { includeRevoked: false });
  const grant = useGrantProductAccess(userId, membershipId);
  const revoke = useRevokeProductAccess(userId, membershipId);

  const [checked, setChecked] = useState<Set<string>>(new Set(grantedProductIds));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset state when the modal opens or grants change.
  useEffect(() => {
    if (opened) {
      setChecked(new Set(grantedProductIds));
      setError(null);
    }
  }, [opened, grantedProductIds]);

  const availableAssignments = (assignmentsQuery.data ?? []).filter((a) => a.status === 'active');

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    setError(null);
    setSaving(true);
    const granted = new Set(grantedProductIds);
    const toGrant = [...checked].filter((id) => !granted.has(id));
    const toRevoke = [...granted].filter((id) => !checked.has(id));

    const results = await Promise.allSettled([
      ...toGrant.map((id) => grant.mutateAsync(id)),
      ...toRevoke.map((id) => revoke.mutateAsync(id)),
    ]);
    const failures = results.filter((r) => r.status === 'rejected');
    setSaving(false);

    if (failures.length === 0) {
      onClose();
    } else {
      setError(`Some changes failed (${failures.length} of ${results.length}).`);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Products for ${orgName}`}
      transitionProps={{ duration: 0 }}
    >
      <Stack>
        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {error}
          </Alert>
        )}
        {availableAssignments.length === 0 ? (
          <Stack gap="xs">
            <Text>No active product assignments for this organisation.</Text>
            <Anchor component={Link} to={`/orgs/${orgId}/products`}>
              Assign one first
            </Anchor>
          </Stack>
        ) : (
          <Stack>
            {availableAssignments.map((a) => (
              <Checkbox
                key={a.productId}
                checked={checked.has(a.productId)}
                onChange={() => toggle(a.productId)}
                label={`${a.productCode} (${a.productDisplayName})`}
              />
            ))}
          </Stack>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={availableAssignments.length === 0}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
